<script setup>
import { computed, onMounted, onUnmounted, onBeforeMount, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { findOuterIndexByReportId } from './utils'
import { getDeviceLinkMode } from './config/keyboardDevices.js'
import LoadingOverlay from
  '@/components/LoadingOverlay.vue'
import leftSider from './components/leftSider/index.vue'
import { useKeyboardStore } from './stores/keyboard'
import { useDeviceStore } from './stores/device';
import { useMacroStore } from './stores/macro'
import { CheckOutlined, CopyOutlined, ExclamationCircleOutlined } from '@ant-design/icons-vue';
import { createVNode } from 'vue';
import { Modal } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';
import toolTip from '@/components/tootip/index.vue'
import { INITIAL_IMAGE_HASH_ARRAY } from '@/constants/imageHashes'
import Icon from '@/components/Icon.vue'
// import { getDeviceLinkMode } from './config/keyboardDevices.js'

// 获取设备模式
const mode = import.meta.env.VITE_DEVICE_MODE || 'webhid'
const isDevDebug = import.meta.env.DEV
console.log(`[App.vue] 设备模式: ${mode}`)
const isLoading = ref(false)
const isAppReady = ref(false)  // 标志：App 初始化完成
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const keyboardStore = useKeyboardStore()
const deviceStore = useDeviceStore()
const macroStore = useMacroStore()
const isSingleFileMode = import.meta.env.MODE === 'singlefile'
const isVsdServer = import.meta.env.VITE_SERVER_TYPE === 'vsd'
const timeID = ref(null)
const AMAZON_EMAIL = 'amazon@mirabox.net'
const amazonEmailCopied = ref(false)
let amazonEmailCopyTimer = null
const dockConnectModal = ref(null)  // 用于追踪 Dock 连接对话框
const showDebugPanel = ref(false)
const debugEvents = ref([])
const MAX_DEBUG_EVENTS = 60

const pushDebugEvent = (type, detail = '') => {
  if (!isDevDebug) return
  const timestamp = new Date().toLocaleTimeString('en-GB')
  debugEvents.value.unshift({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp,
    type,
    detail
  })
  if (debugEvents.value.length > MAX_DEBUG_EVENTS) {
    debugEvents.value = debugEvents.value.slice(0, MAX_DEBUG_EVENTS)
  }
}

const clearDebugEvents = () => {
  debugEvents.value = []
}

// Tooltip状态管理
const tooltipState = ref({
  visible: false,
  x: 0,
  y: 0,
  content: '',
  onItemClick: null
})

// 全局显示tooltip方法
const showTooltip = (x, y, content, onItemClick = null) => {
  tooltipState.value = {
    visible: true,
    x,
    y,
    content,
    onItemClick
  }
}

// 全局隐藏tooltip方法
const hideTooltip = () => {
  tooltipState.value.visible = false
}

// 将方法挂载到全局
window.showTooltip = showTooltip
window.hideTooltip = hideTooltip

// 项目初始化时设置主题
const initTheme = () => {
  const savedTheme = localStorage.getItem('theme') || 'dark'
  document.documentElement.setAttribute('data-theme', savedTheme)
}

// 计算属性：不是 /auth 就显示侧边栏
// 添加 isAppReady 检查，防止初始化时闪现
const showLeft = computed(
  () => isAppReady.value && !(mode !== 'webhid' || route.name === 'auth')
)
const showHeader = computed(() => isAppReady.value && !(route.name === 'auth'))

// 检查是否为 VSD 设备
const isVsdDevice = computed(() => {
  return deviceStore.deviceVendorId === 0x5548 && deviceStore.deviceProductId === 0x1025
})

const supportsReportId = (device, reportId = 4) => {
  return findOuterIndexByReportId([device], reportId) !== -1
}

onBeforeMount(async () => {
  // 初始化主题
  initTheme()

  try {
    if (mode === 'webhid') {
      // WebHID 模式: 检查是否有已授权的设备
      const devices = await navigator.hid.getDevices()
      console.log('[App.vue] 已授权设备:', devices)

      if (!devices.length) {
        console.log('[App.vue] 没有已授权设备')
        return
      }

      const supportedDevices = devices.filter(device => supportsReportId(device, 4))
      if (!supportedDevices.length) {
        console.log('[App.vue] 未找到支持的设备')
        return
      }

      const wiredDevice = supportedDevices.find(device =>
        getDeviceLinkMode(device.vendorId, device.productId) === 'wired'
      )
      const wirelessDevice = supportedDevices.find(device =>
        getDeviceLinkMode(device.vendorId, device.productId) === 'wireless'
      )

      let selectedDevice = null
      if (wiredDevice && wirelessDevice) {
        // 你的场景: 当前为有线时，授权列表会同时出现有线+无线
        selectedDevice = wiredDevice
        console.log('[App.vue] 检测到有线+无线设备，判定当前为有线模式，优先连接有线设备')
      } else if (!wiredDevice && wirelessDevice) {
        // 你的场景: 当前为无线时，授权列表只出现无线
        selectedDevice = wirelessDevice
        console.log('[App.vue] 仅检测到无线设备，判定当前为无线模式，连接无线设备')
      } else if (wiredDevice) {
        selectedDevice = wiredDevice
        console.log('[App.vue] 仅检测到有线设备，连接有线设备')
      } else {
        selectedDevice = supportedDevices[0]
        console.log('[App.vue] 设备模式未知，使用第一个支持设备作为兜底')
      }

      // 连接设备，但不手动跳转路由
      // 路由守卫会自动检测设备状态并导航
      const success = await deviceStore.setDevice(selectedDevice)
      if (success) {
        if (route.name === 'auth') {
          router.replace('/keyboard')
        }
        console.log('[App.vue] 设备连接成功，路由守卫将处理导航')
        // 不再手动 router.push，让路由守卫处理
      } else {
        console.error('[App.vue] 设备连接失败，回到授权页')
        router.replace('/auth')
      }
      return
    }

    // WebSocket 模式: 直接连接，成功后进入 keyboard
    console.log('[App.vue] WebSocket 模式 - 直接连接')
    const success = await deviceStore.setDevice(null)
    if (success) {
      router.replace('/keyboard')
    } else {
      console.error('[App.vue] WebSocket 连接失败，回到授权页')
      router.replace('/auth')
    }
  } catch (error) {
    console.error('[App.vue] 启动连接异常:', error)
    router.replace('/auth')
  }
})
onMounted(() => {
  // const params = new URLSearchParams(window.location.search)
  // const deviceName = params.get('deviceName')
  // const deviceUUID = params.get('deviceUUID')
  // const serialNumber = params.get('serialNumber')
  // if (deviceName || deviceUUID || serialNumber) {
  //   Modal.info({
  //     title: 'Launch Params',
  //     content: `deviceName: ${deviceName || ''}\ndeviceUUID: ${deviceUUID || ''}\nserialNumber: ${serialNumber || ''}`
  //   })
  // }

  // 获取设备模式
  const mode = import.meta.env.VITE_DEVICE_MODE || 'webhid'

  if (mode === 'webhid') {
    // 仅在 WebHID 模式下监听设备断开事件
    navigator.hid.addEventListener("disconnect", event => {
      console.log('[App.vue] WebHID 设备断开:', event)
      pushDebugEvent('hid.disconnect', `${event?.device?.vendorId || '-'}:${event?.device?.productId || '-'}`)
      deviceStore.closeDevice()
      router.replace({ name: 'auth' })
    });
  }

  // 监听设备上报的链路模式并尝试自动切换连接（如有线<->无线）
  watch(
    () => deviceStore.reportedLinkMode,
    async (reportedMode) => {
      if (mode !== 'webhid' || !reportedMode) return
      pushDebugEvent('mode.reported', `reported=${reportedMode}, current=${deviceStore.currentLinkMode}`)
      const result = await deviceStore.handleReportedLinkMode(reportedMode)
      if (!result?.success && deviceStore.needReauthorize) {
        console.warn('[App.vue] 自动切换失败，需用户重新授权目标模式设备')
        pushDebugEvent('mode.switch.fail', result?.reason || 'need_reauthorize')
        router.replace({ name: 'auth' })
      } else if (result?.success && !result?.skipped) {
        pushDebugEvent('mode.switch.ok', `to=${reportedMode}`)
      }
    }
  )

  watch(
    () => [deviceStore.currentLinkMode, deviceStore.reportedLinkMode, deviceStore.needReauthorize, deviceStore.isOpened, deviceStore.deviceVendorId, deviceStore.deviceProductId],
    ([current, reported, reauth, opened, vid, pid]) => {
      pushDebugEvent('state', `opened=${opened}, current=${current}, reported=${reported}, reauth=${reauth}, vid=${vid}, pid=${pid}`)
    },
    { immediate: true }
  )

  // 监听 Dock 连接状态（仅在 WebHID 模式下显示弹框，上位机模式不显示）
  watch(
    () => deviceStore.dockConnect,
    (newValue) => {
      console.log('[App.vue] Dock 连接状态改变:', newValue)
      if (newValue === 'Connected' && mode === 'webhid') {
        // 仅在 WebHID 模式下显示 Dock 连接确认对话框
        dockConnectModal.value = Modal.confirm({
          title: t('common.tips5'),
          icon: createVNode(ExclamationCircleOutlined),
          footer: null,
          keyboard: false,
          okType: 'danger',
        })
      } else if (newValue === 'Disconnected') {
        // Dock 断开连接，关闭对话框
        console.log('[App.vue] Dock 已断开连接，关闭对话框')
        if (dockConnectModal.value) {
          dockConnectModal.value.destroy()
          dockConnectModal.value = null
        }
      }
    }
  )

  // navigator.hid.addEventListener("connect", async (event) => {
  //   console.log(event, 'connect')
  //   const devices = await navigator.hid.getDevices()
  //   if (devices.length) {
  //     const index = findOuterIndexByReportId(devices, 4)
  //     const success = await deviceStore.setDevice(devices[index]) // 设置设备
  //     if (success) {
  //       isLoading.value = true
  //       setTimeout(() => {
  //         isLoading.value = false
  //         router.push('/keyboard') // 连接成功才跳转
  //       }, 500)
  //     } else {
  //       console.error('设备连接失败，跳转到授权页面')
  //       router.replace('/auth') // 设备连接失败时跳转到授权页面
  //     }
  //   } else {
  //     router.replace('/auth') // 没有设备时跳转到授权页面
  //   }
  //   // Automatically open event.device or warn user a device is available.
  // });

  // timeID.value = setInterval(() => {
  //   if (!deviceStore.device) {
  //     // 当前路由回到auth
  //     router.replace({ name: 'auth' })
  //   }
  // }, 3000)

  // ✨ 新增：等待路由完全准备好后，标记 App 已初始化完成
  // 这样可以防止组件在路由重定向时闪现
  router.isReady().then(() => {
    console.log('[App.vue] 路由已就绪，标记 App 初始化完成')
    isAppReady.value = true
  })

})
onUnmounted(() => {
  clearInterval(timeID.value)
  clearTimeout(amazonEmailCopyTimer)
})
const resetCustomImageStorage = () => {
  const imageKeyRegex = /^fnPage([4-6])CustomImage_\d+$/
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && imageKeyRegex.test(key)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))
  localStorage.setItem('CustomImageHashArray', JSON.stringify([...INITIAL_IMAGE_HASH_ARRAY]))
  console.log(`[App.vue] 已清理本地自定义图片缓存: ${keysToRemove.length} 项，并重置 CustomImageHashArray`)
}
const handleReset = () => {
  Modal.confirm({
    title: t('common.tips4'),
    icon: createVNode(ExclamationCircleOutlined),
    content: '',
    okText: t('common.yes'),
    okType: 'danger',
    cancelText: t('common.no'),
    onOk() {
      console.log('OK');
      deviceStore.sendResetEffect()
      keyboardStore.resetKeyCodeJson()
      macroStore.resetEffect()
      resetCustomImageStorage()
    },
    onCancel() {
      console.log('Cancel');
    },
  });

}
const contentStyle = {
  textAlign: 'center',
  minHeight: 120,
  lineHeight: '120px',
  color: '#fff',
  backgroundColor: '#108ee9',
};
const siderStyle = {
  textAlign: 'center',
  lineHeight: '120px',
  color: '#fff',
  backgroundColor: '#3ba0e9',
};

const intoConfig = () => {
  // router.push('/configuration')
}
const handleDownload = () => {
  window.open('https://download.mirabox.net');
};
const copyAmazonEmail = async () => {
  let copied = false

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(AMAZON_EMAIL)
      copied = true
    } catch (error) {
      console.warn('[App.vue] Clipboard API copy failed:', error)
    }
  }

  if (!copied) {
    const textarea = document.createElement('textarea')
    textarea.value = AMAZON_EMAIL
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()

    try {
      copied = document.execCommand('copy')
    } finally {
      document.body.removeChild(textarea)
    }
  }

  if (copied) {
    amazonEmailCopied.value = true
    clearTimeout(amazonEmailCopyTimer)
    amazonEmailCopyTimer = setTimeout(() => {
      amazonEmailCopied.value = false
    }, 2000)
  }
}
const handleUrl = (type) => {
  const urlObj = {
    X: 'https://x.com/MiraBoxEco',
    Facebook: 'https://www.facebook.com/people/MiraBox/61593817958911/',
    INS: 'https://www.instagram.com/mirabox_global',
    Linkedin: 'https://www.linkedin.com/company/miraboxecosystem/',
    Discord: 'https://discord.com/servers/space-community-1194117579392950333',
    reddit: 'https://www.reddit.com/r/MiraBox_Global/'
  }
  window.open(urlObj[type])
}
</script>

<template>
  <LoadingOverlay :visible="isLoading" text="正在连接设备..." />
  <toolTip :visible="tooltipState.visible" :x="tooltipState.x" :y="tooltipState.y" :content="tooltipState.content"
    :onItemClick="tooltipState.onItemClick" />
  <a-config-provider>
    <div class="app-container">
      <leftSider v-if="showLeft" />
      <div class="page-container">
        <div v-if="showHeader" class="fixTop">
          <div class="sc-VLMBG cgdrcG">
            <div class="leftBox">
              <div class="mBox">{{ deviceStore.lightData.keyboardType === 'W' ? 'M2' : 'M1' }}</div>
              <p @click="intoConfig" class="leftName pointer">{{ deviceStore.lightData.keyboardType === 'W' ? 'Windows'
                : 'Mac' }}</p>
              <Icon tooltip="Twitter" v-if="!isSingleFileMode && !isVsdServer" @click="handleUrl('X')" name="X"
                class="icon-svg"></Icon>
              <Icon tooltip="Facebook" v-if="!isSingleFileMode && !isVsdServer" @click="handleUrl('Facebook')"
                name="Facebook" class="icon-svg"></Icon>
              <Icon tooltip="Instagram" v-if="!isSingleFileMode && !isVsdServer" @click="handleUrl('INS')" name="INS"
                class="icon-svg">
              </Icon>
              <Icon tooltip="LinkedIn" v-if="!isSingleFileMode && !isVsdServer" @click="handleUrl('Linkedin')"
                name="Linkedin" class="icon-svg"></Icon>
              <Icon tooltip="Discord" v-if="!isSingleFileMode && !isVsdServer" @click="handleUrl('Discord')"
                name="Discord" class="icon-svg"></Icon>
              <Icon tooltip="Reddit" v-if="!isSingleFileMode && !isVsdServer" @click="handleUrl('reddit')" name="reddit"
                class="icon-svg"></Icon>
              <!-- <a-popover v-if="!isSingleFileMode && !isVsdServer" placement="bottomLeft" trigger="hover"
                :mouse-enter-delay="0.15" :mouse-leave-delay="0.15" :arrow="false">
                <template #content>
                  <div class="amazon-email-content">
                    <span>{{ AMAZON_EMAIL }}</span>
                    <button type="button" class="amazon-email-copy"
                      :aria-label="amazonEmailCopied ? 'Email copied' : 'Copy email address'"
                      :title="amazonEmailCopied ? 'Email copied' : 'Copy email address'" @click.stop="copyAmazonEmail">
                      <CheckOutlined v-if="amazonEmailCopied" />
                      <CopyOutlined v-else />
                    </button>
                  </div>
                </template>
<button type="button" class="amazon-icon-trigger" aria-label="Amazon contact email">
  <Icon name="amazon"></Icon>
</button>
</a-popover> -->
            </div>
            <div class="right">
              <a v-if="!isSingleFileMode && !isVsdServer" href="https://download.mirabox.net" target="_blank"
                rel="noopener noreferrer">
                <span>{{ $t('common.appDownload') }}</span>
              </a>
              <!-- <span @click="handleDownload">{{ $t('common.appDownload') }}</span> -->
              <div class="coloursLoadingBac">
                <p v-show="deviceStore.sendReportTime">{{ $t('common.tips3') }} {{ deviceStore.sendReportTime }}</p>
              </div>
              <div class="sc-bRKDuR hrhPRq" @click="handleReset">
                <div class="btnInside white">
                  <div style="flex: 1 1 0%; text-align: center;">
                    <p style="white-space: nowrap; line-height: 1em;">{{ $t('common.restoreDefault') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <RouterView />
      </div>
      <div v-if="isDevDebug" class="debug-panel" :class="{ open: showDebugPanel }">
        <button class="debug-toggle" @click="showDebugPanel = !showDebugPanel">
          {{ showDebugPanel ? 'Hide Debug' : 'Show Debug' }}
        </button>
        <div v-if="showDebugPanel" class="debug-body">
          <div class="debug-header">
            <strong>HID Debug</strong>
            <button class="debug-clear" @click="clearDebugEvents">Clear</button>
          </div>
          <div class="debug-kv">
            <div>isOpened: {{ deviceStore.isOpened }}</div>
            <div>currentLinkMode: {{ deviceStore.currentLinkMode }}</div>
            <div>reportedLinkMode: {{ deviceStore.reportedLinkMode }}</div>
            <div>needReauthorize: {{ deviceStore.needReauthorize }}</div>
            <div>VID/PID: {{ deviceStore.deviceVendorId }} / {{ deviceStore.deviceProductId }}</div>
          </div>
          <div class="debug-list">
            <div v-for="item in debugEvents" :key="item.id" class="debug-item">
              <span class="debug-time">[{{ item.timestamp }}]</span>
              <span class="debug-type">{{ item.type }}</span>
              <span class="debug-detail">{{ item.detail }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- <div v-if="showLayout" class="sidebar"></div> -->
    </div>

  </a-config-provider>
</template>
<style lang="scss">
.sidebar {
  z-index: 103;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  width: 13.5rem;
  height: 100%;
  padding: 0.5rem;
  gap: 0.5rem;
  flex-shrink: 0;
  border-radius: var(--border-radius-xl);
  background: var(--background-sidebar-white);
  position: relative;
  transition: 0.25s;
}

.sidebar.collapsed {
  width: 4.5rem;
}

.gDWPlU {
  display: flex;
  min-width: 1.5rem;
  min-height: 1.5rem;
  padding: 0.125rem 0.25rem;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  gap: 0.125rem;
  border-radius: var(--border-radius-s);
  position: relative;
  cursor: pointer;

  .nav-button-handle {
    opacity: 0;

    span,
    div {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .nav-button-icon,
  .nav-button-handle {
    height: auto;
    max-height: 100%;
    width: auto;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .nav-button-badge {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    padding: 0.0625rem 0.5rem 0.09375rem;
    border-radius: 0.375rem;
    font-size: 0.625rem;
    font-weight: 900;
    text-transform: capitalize;
    background: var(--button-black-l);
    color: var(--text-white-l-title);
    line-height: normal;
    letter-spacing: 0.12em;
  }

  .nav-button-content {
    display: flex;
    align-items: center;
    height: 100%;
    border-radius: var(--border-radius-xs);
    transition: background 0.3s;
    padding: 0rem 0.125rem;
    gap: 0.5rem;
    flex: 1 0 0px;
    overflow: hidden;
  }

  .nav-button-name {
    height: auto;
    flex: 1 1 0%;
  }
}

.gDWPlU.active {
  background: var(--button-black-l);
}

.eZoCVx {
  color: var(--text-white-l-title);
  overflow: hidden;
  white-space: nowrap;
  text-transform: capitalize;
  line-height: normal;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  font-weight: 900;
}

.bDNdxN {
  color: var(--text-black-l-title);
  overflow: hidden;
  white-space: nowrap;
  text-transform: capitalize;
  line-height: normal;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  font-weight: 900;
}

.kTbYFY {
  font-size: 0.75rem;
  font-weight: 700;
}

.gdasrc {
  display: inline-block;
  line-height: normal;
  color: var(--text-black-s-content);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.0075rem;
}

.hyNOSv,
.hSINmI {
  display: inline-block;

  .btnInside.dark {
    background-color: var(--button-black-l);
    color: var(--text-white-l-title);
  }

  .btnInside.white {
    background-color: var(--button-white-xl);
    color: var(--text-black-l-title);
  }

  .btnInside.small {
    padding: 0px 0.5rem;
    border-radius: 0.375rem;
    height: 1.3125rem;
  }

  .btnInside.danger {
    background-color: rgb(255, 66, 0);
    color: var(--text-white-l-title);
  }

  .btnInside {
    width: 100%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    padding: 0px 0.75rem 11.5556px;
    border-radius: 0.5rem;
    height: 1.5625rem;
    max-height: 100%;


  }
}

.rKVPaR {
  display: flex;
  min-width: 1.5rem;
  min-height: 1.5rem;
  padding: 0.125rem 0.25rem;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  gap: 0.125rem;
  border-radius: var(--border-radius-s);
  position: relative;
  cursor: pointer;

  .nav-button-badge {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    padding: 0.0625rem 0.5rem 0.09375rem;
    border-radius: 0.375rem;
    font-size: 0.625rem;
    font-weight: 900;
    text-transform: capitalize;
    background: var(--button-black-l);
    color: var(--text-white-l-title);
    line-height: normal;
    letter-spacing: 0.12em;
  }

  .nav-button-content {
    display: flex;
    align-items: center;
    height: 100%;
    border-radius: var(--border-radius-xs);
    transition: background 0.3s;
    padding: 0rem 0.125rem;
    gap: 0.5rem;
    flex: 1 0 0px;
    overflow: hidden;
  }

  .nav-button-name {
    height: auto;
    flex: 1 1 0%;
  }

  .nav-button-handle {
    opacity: 0;
  }

  .nav-button-icon,
  .nav-button-handle {
    height: auto;
    max-height: 100%;
    width: auto;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.collapsed :not(:first-child) {
    opacity: 0;
    position: absolute;
    width: 100%;
    z-index: -1;
  }
}

.ivRIVu {
  display: inline-block;
  line-height: normal;
  color: var(--text-black-l-title);
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.0075rem;
}

.ant-popover .ant-popover-inner {
  background-color: var(--box-nested-white-xl-max);
}

.ant-modal-confirm .ant-modal-confirm-body .ant-modal-confirm-title {
  color: var(--text-black-l-title);
}

.ant-btn-default {
  color: var(--text-black-l-title);
}

.tooltip-inner {
  background-color: var(--box-nested-white-xl-max);
  color: var(--text-black-l-title);
}

.ant-modal-content {
  border-radius: 1.25rem !important;
  overflow: hidden;
  padding: .75rem !important;
}

.ant-modal-content {
  background-color: var(--box-nested-white-xl-max) !important;
}

.ant-modal .ant-modal-header {
  color: rgba(0, 0, 0, 0.88);
  background: #ffffff;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-bottom: 0.5rem;
  padding: 0;
  border-bottom: none;
}

.modal_title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 1.25rem;
}
</style>
<style lang="scss" scoped>
.app-container {
  display: flex;
  height: 100%;
  -ms-flex-pack: justify;
  -webkit-box-pack: justify;
  justify-content: space-between;
  // padding: 0.5rem;
  background-color: var(--box-nested-white-xl-max);
  background-image: var(--app-background-image);
  background-size: cover;
  overflow: hidden;
  position: relative;
}

.page-container {
  display: flex;
  -ms-flex-direction: column;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  margin: 0 auto;
  height: 100%;
  width: 100%;
  // margin: 0 0.5rem;
  overflow: hidden;
  position: relative;
}

.fixTop {
  position: sticky;
  top: 0;
  z-index: 99;
  width: 100%;
  height: auto;
}

.cgdrcG {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 1.5625rem;
  margin: 0.25rem 0px 0.625rem;
  width: 100%;

  .leftBox {
    width: 30%;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .leftName {
    font-size: 0.875rem;
    font-weight: bold;
    color: var(--text-black-l-title);
  }

  .right {
    height: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
  }

  .coloursLoadingBac {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--text-black-s-content);
    font-size: 0.75rem;
  }

  .mBox {
    background-color: var(--button-black-l);
    border-radius: 0.375rem;
    padding: 0.1875rem 0.375rem 0.125rem;
    color: rgb(255, 255, 255);
    font-size: 0.75rem;
    font-weight: bold;
  }
}

.hrhPRq {
  display: inline-block;

  .btnInside.white {
    background-color: var(--button-white-xl);
    color: var(--text-black-l-title);
  }

  .btnInside {
    width: 100%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    padding: 0px 0.75rem;
    border-radius: 0.5rem;
    height: 1.5625rem;
    max-height: 100%;
  }
}

.debug-panel {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.debug-toggle {
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  background: #1f2937;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
}

.debug-body {
  margin-top: 8px;
  width: 360px;
  max-height: 42vh;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(15, 23, 42, 0.92);
  color: #e5e7eb;
  display: flex;
  flex-direction: column;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  font-size: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.debug-clear {
  border: none;
  border-radius: 6px;
  padding: 2px 8px;
  background: rgba(239, 68, 68, 0.85);
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}

.debug-kv {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3px;
  padding: 8px 10px;
  font-size: 11px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.debug-list {
  overflow: auto;
  padding: 6px 10px 10px;
  font-size: 11px;
}

.debug-item {
  margin-bottom: 4px;
  line-height: 1.35;
}

.debug-time {
  color: #93c5fd;
  margin-right: 6px;
}

.debug-type {
  color: #fde68a;
  margin-right: 6px;
}

.debug-detail {
  color: #d1d5db;
  word-break: break-all;
}

.leftBox :deep(.icon-svg) {
  color: #00bd7e;
  cursor: pointer;

  &:hover {
    color: var(--icon-black-l-title);
  }
}

.amazon-email-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 12rem;
  color: var(--text-black-l-title);
  font-size: 0.75rem;
  line-height: 1.25rem;

  span {
    flex: 1;
    white-space: nowrap;
  }
}

.amazon-icon-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #00bd7e;
  cursor: pointer;

  :deep(.icon-svg) {
    display: block;
  }

  &:hover,
  &:focus-visible {
    color: var(--icon-black-l-title);
    outline: none;
  }
}

.amazon-email-copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: 0;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--text-black-s-content);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--button-black-l);
    color: var(--text-white-l-title);
    outline: none;
  }
}
</style>
